import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Field, Form, FormMessage, ModalCard } from '~/components';
import FixedDropdown from '~/components/FixedDropdown';
import FixedSingleSelect from '~/components/FixedSingleSelect';
import { useApi, useWorkspace } from '~/contexts';
import { useDirtyCheck, useFeatures, useForm } from '~/hooks';
import { emptyStringToNull } from '~/utils';

const Body = styled(ModalCard.Body)`
  && {
    padding: 0;
  }
`;

const Scroller = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 1.625rem;
`;

function MemberInviteForm({ onClose, onInvited }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const [{ isSubmitting, message, status }, form] = useForm();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [defaultSecurityRoleId, setDefaultSecurityRoleId] = useState('');
  const [memberTemplates, setMemberTemplates] = useState([]);

  const fetchMemberTemplates = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).memberTemplates().get({ size: 1000 });
      setMemberTemplates(data);
    } catch {
      setMemberTemplates([]);
    }
  }, [api, workspace.id]);

  useEffect(() => {
    fetchMemberTemplates();
  }, [fetchMemberTemplates]);

  async function handleSubmit(values) {
    try {
      form.submit();

      const omit = ['businessUnit', 'practice', 'manager', 'templateRequiresManager'];
      const body = emptyStringToNull({
        ..._.omit(values, omit),
        managerId: values.manager?.id ?? null,
        businessUnitId: values.businessUnit?.id ?? null,
        practiceId: values.practice?.id ?? null,
      });

      if (values.memberTemplateId) {
        delete body.costMethodId;
        delete body.receiveMissingTimeReminders;
        delete body.timeOffAllowed;
        delete body.unsubmittedTimesheetReminders;
      }

      const { data } = await api.www.workspaces(workspace.id).members().invite(body);

      await onInvited(data);
      form.done();
    } catch ({ message }) {
      return form.error({ message });
    }
  }

  const handleClose = () => dirtyCheck(onClose);

  return (
    <ModalCard title="New Member" onClose={handleClose}>
      <Formik
        innerRef={formRef}
        initialValues={{
          memberTemplateId: '',
          templateRequiresManager: false,
          costMethodId: 'fixed_hourly',
          email: '',
          employmentTypeId: 'employee',
          isBillable: true,
          loginEnabled: true,
          manager: null,
          name: '',
          businessUnit: null,
          practice: null,
          receiveMissingTimeReminders: true,
          unsubmittedTimesheetReminders: true,
          securityRoleId: '',
          sendEmail: true,
          timeOffAllowed: true,
        }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .label('Email')
            .email()
            .test('validate-email', '', async function (email) {
              // We need to create the error with createError so that we can use "email" in the message.
              // If we use the argument message of test(name, message, func) to create the message we cannot display the email.
              const { path, createError } = this;

              const isEmailValid = await Yup.object()
                .shape({
                  email: Yup.string().email().required(),
                })
                .isValid({ email });

              if (!isEmailValid) return true;

              try {
                await api.www.workspaces(workspace.id).members().isEmailAvailable({ email });
                return true;
              } catch {
                return createError({
                  path,
                  message: `A member with email ${email} already exists in this workspace.`,
                });
              }
            })
            .required(),
          employmentTypeId: Yup.string().label('Member Type').required(),
          manager: Yup.object()
            .label('Manager')
            .nullable()
            .when('templateRequiresManager', {
              is: true,
              then: (schema) => schema.required('Manager is required by the selected Member Template'),
            }),
          name: Yup.string().label('Name').max(255).required(),
          securityRoleId: Yup.string().label('Security Role').required(),
        })}>
        {(formik) => {
          const selectedTemplate = memberTemplates.find((template) => template.id === formik.values.memberTemplateId);

          const commitValues = (values, template = selectedTemplate) => {
            values.templateRequiresManager =
              !!template &&
              (template.internalExpenseApprovalMode === 'manager' ||
                (template.timeOffApprovalMode === 'manager' && template.timeOffAllowed === true));

            formik.setValues(values);

            if (!values.templateRequiresManager || values.manager) {
              formik.setFieldTouched('manager', false, false);
            }
          };

          const handleLoginEnabledChange = ({ target: { checked } }) => {
            formik.setValues({
              ...formik.values,
              loginEnabled: checked,
              sendEmail: checked,
            });
          };

          const handleEmploymentTypeChange = ({ target: { value } }) => {
            commitValues({
              ...formik.values,
              employmentTypeId: value,
            });
          };

          const handleTemplateChange = ({ target: { value } }) => {
            const template = memberTemplates.find((item) => item.id === value) ?? null;
            const employmentTypeId = template?.employmentTypeId ?? 'employee';
            const values = {
              ...formik.values,
              memberTemplateId: template?.id ?? '',
              employmentTypeId,
              isBillable: template?.isBillable ?? true,
              manager: null,
              practice: template?.practiceId ? template.practice : null,
              businessUnit: template?.businessUnitId ? template.businessUnit : null,
              securityRoleId: template?.securityRoleId ?? defaultSecurityRoleId,
            };

            commitValues(values, template);
          };

          return (
            <Form data-testid="invite_member_form">
              <Body>
                <Scroller>
                  <Form.Control>
                    <Field.Text autoFocus name="name" placeholder="Name" maxLength={255} />
                  </Form.Control>

                  <Form.Control>
                    <Field.Text
                      name="email"
                      type="email"
                      placeholder="Email"
                      onBlur={() => {
                        formik.setFieldTouched('email', true, false);
                        formik.validateField('email');
                      }}
                    />
                  </Form.Control>

                  {memberTemplates.length > 0 && (
                    <Form.Control>
                      <Field.MemberTemplateSelect
                        name="memberTemplateId"
                        placeholder="Member Template"
                        showEmptyOption
                        component={FixedSingleSelect}
                        templates={memberTemplates}
                        onChange={handleTemplateChange}
                      />
                    </Form.Control>
                  )}

                  <Form.Control>
                    <Field.EmploymentTypeSelect
                      name="employmentTypeId"
                      placeholder="Member Type"
                      component={FixedSingleSelect}
                      onChange={handleEmploymentTypeChange}
                    />
                  </Form.Control>

                  <Form.Control>
                    <Field.Checkbox name="isBillable" label="This is a billable member" />
                  </Form.Control>

                  <Form.Control>
                    <Field.SecurityRoleSelect
                      name="securityRoleId"
                      placeholder="Security Role"
                      component={FixedSingleSelect}
                      useDefault={!formik.values.memberTemplateId}
                      onDefault={(defaultValue) => {
                        setDefaultSecurityRoleId(defaultValue);
                        if (!formRef.current.dirty) {
                          formRef.current.resetForm({
                            values: { ...formRef.current.values, securityRoleId: defaultValue },
                          });
                        }
                      }}
                    />
                  </Form.Control>

                  {features.businessUnits && (
                    <Form.Control>
                      <Field.BusinessUnitSelect
                        name="businessUnit"
                        placeholder="Business Unit"
                        component={FixedDropdown}
                      />
                    </Form.Control>
                  )}

                  {features.practices && (
                    <Form.Control>
                      <Field.PracticeSelect name="practice" placeholder="Practice" allowNew component={FixedDropdown} />
                    </Form.Control>
                  )}

                  <Form.Control>
                    <Field.MemberSelect name="manager" placeholder="Manager" component={FixedDropdown} />
                  </Form.Control>

                  <Form.Control>
                    <Field.Checkbox
                      name="loginEnabled"
                      label="Allow this member to log in to the workspace"
                      onChange={handleLoginEnabledChange}
                    />
                  </Form.Control>

                  <Form.Control>
                    <Field.Checkbox
                      name="sendEmail"
                      label="Send an email invitation to this member now"
                      disabled={!formik.values.loginEnabled}
                    />
                  </Form.Control>

                  {status && <FormMessage.Error>{message}</FormMessage.Error>}
                </Scroller>
              </Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={handleClose}>Close</CancelButton>
                  <Button isLoading={isSubmitting} onClick={formik.submitForm}>
                    Save
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          );
        }}
      </Formik>
    </ModalCard>
  );
}

export default MemberInviteForm;
